import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import { useAdminUserDetails, useUpdateUserTier, useUpdateUserPermissions } from '@/queries/admin';
import { TIER_NAMES } from '@/types/subscription';
import type { SubscriptionTier } from '@/types/subscription';
import styles from './UserEditDrawer.module.scss';

interface UserEditDrawerProps {
  userId: string | null;
  onClose: () => void;
}

export function UserEditDrawer({ userId, onClose }: UserEditDrawerProps) {
  const { data: user, isLoading } = useAdminUserDetails(userId);
  const updateTier = useUpdateUserTier();
  const updatePermissions = useUpdateUserPermissions();

  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('foundation');
  const [isAdmin, setIsAdmin] = useState(false);

  // Update local state when user data loads
  useEffect(() => {
    if (user) {
      setSelectedTier(user.subscription?.tier || 'foundation');
      setIsAdmin(user.permission_level.admin);
    }
  }, [user]);

  const handleUpdateTier = async () => {
    if (!userId) return;

    try {
      await updateTier.mutateAsync({
        userId,
        tier: selectedTier,
      });
      alert('Tier updated successfully');
    } catch (error) {
      alert(`Failed to update tier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleUpdatePermissions = async () => {
    if (!userId) return;

    try {
      await updatePermissions.mutateAsync({
        userId,
        permissions: {
          admin: isAdmin,
          roles: isAdmin ? ['super_admin'] : [],
        },
      });
      alert('Permissions updated successfully');
    } catch (error) {
      alert(`Failed to update permissions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const hasStripeSubscription = user?.subscription?.stripe_subscription_id;
  const tierChanged = selectedTier !== (user?.subscription?.tier || 'foundation');
  const permissionsChanged = isAdmin !== user?.permission_level.admin;

  return (
    <AnimatePresence>
      {userId && (
        <motion.div
          className={styles.drawer}
          initial={{ x: '100%' }}
          animate={{
            x: 0,
            transition: {
              type: 'spring',
              damping: 20,
              stiffness: 300,
              mass: 0.8,
            },
          }}
          exit={{
            x: '100%',
            transition: {
              type: 'spring',
              damping: 25,
              stiffness: 300,
            },
          }}
        >
          <div className={styles.drawer__header}>
            <h2 className={styles.drawer__title}>Edit User</h2>
            <button
              className={styles.drawer__close}
              onClick={onClose}
              aria-label="Close drawer"
            >
              ✕
            </button>
          </div>

          {isLoading && (
            <div className={styles.drawer__loading}>
              Loading user details...
            </div>
          )}

          {user && (
            <div className={styles.drawer__content}>
              {/* User Info Section */}
              <section className={styles.drawer__section}>
                <h3 className={styles.drawer__sectionTitle}>User Information</h3>
                <div className={styles.drawer__field}>
                  <label className={styles.drawer__label}>Email:</label>
                  <span className={styles.drawer__value}>{user.email}</span>
                </div>
                <div className={styles.drawer__field}>
                  <label className={styles.drawer__label}>User ID:</label>
                  <span className={styles.drawer__valueSmall}>{user.user_id}</span>
                </div>
                <div className={styles.drawer__field}>
                  <label className={styles.drawer__label}>Joined:</label>
                  <span className={styles.drawer__value}>
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </section>

              {/* Subscription Tier Section */}
              <section className={styles.drawer__section}>
                <h3 className={styles.drawer__sectionTitle}>Subscription Tier</h3>

                {hasStripeSubscription && (
                  <div className={styles.drawer__warning}>
                    <span className={styles.drawer__warningIcon}>⚠️</span>
                    <div className={styles.drawer__warningText}>
                      <strong>Active Stripe Subscription Detected</strong>
                      <p>
                        This user has an active Stripe subscription. Manual tier changes may cause
                        billing conflicts. Consider canceling the Stripe subscription first.
                      </p>
                    </div>
                  </div>
                )}

                <div className={styles.drawer__control}>
                  <label htmlFor="tier-select" className={styles.drawer__controlLabel}>
                    Tier:
                  </label>
                  <select
                    id="tier-select"
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value as SubscriptionTier)}
                    className={styles.drawer__select}
                  >
                    <option value="foundation">{TIER_NAMES.foundation} (Free)</option>
                    <option value="deep_dive">{TIER_NAMES.deep_dive} ($4.99/mo)</option>
                    <option value="mastermind">{TIER_NAMES.mastermind} ($7.99/mo)</option>
                  </select>
                </div>

                <button
                  onClick={handleUpdateTier}
                  disabled={!tierChanged || updateTier.isPending}
                  className={styles.drawer__button}
                >
                  {updateTier.isPending ? 'Updating...' : 'Update Tier'}
                </button>
              </section>

              {/* Permissions Section */}
              <section className={styles.drawer__section}>
                <h3 className={styles.drawer__sectionTitle}>Permissions</h3>

                <div className={styles.drawer__control}>
                  <label className={styles.drawer__checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={isAdmin}
                      onChange={(e) => setIsAdmin(e.target.checked)}
                      className={styles.drawer__checkbox}
                    />
                    <span>Admin Access</span>
                  </label>
                  <p className={styles.drawer__help}>
                    Admins can manage users, change subscription tiers, and grant/revoke admin
                    status.
                  </p>
                </div>

                <button
                  onClick={handleUpdatePermissions}
                  disabled={!permissionsChanged || updatePermissions.isPending}
                  className={styles.drawer__button}
                >
                  {updatePermissions.isPending ? 'Updating...' : 'Update Permissions'}
                </button>
              </section>

              {/* Activity Section */}
              <section className={styles.drawer__section}>
                <h3 className={styles.drawer__sectionTitle}>Activity Summary</h3>
                <div className={styles.drawer__field}>
                  <label className={styles.drawer__label}>Total Quizzes Saved:</label>
                  <span className={styles.drawer__value}>{user.profile.total_saved_quizzes}</span>
                </div>
                {user.profile.stripe_customer_id && (
                  <div className={styles.drawer__field}>
                    <label className={styles.drawer__label}>Stripe Customer:</label>
                    <span className={styles.drawer__valueSmall}>{user.profile.stripe_customer_id}</span>
                  </div>
                )}
              </section>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
